<script setup lang="ts">
import type { FormKitFrameworkContext } from '@formkit/core'

const props = defineProps<{
  context: FormKitFrameworkContext & {
    channels: number[]
  }
}>()

const store = useStore()
const route = useRoute()
const router = useRouter()

const allChannels = computed(() =>
  (store.channels || []).map((item) => ({
    value: item.id,
    label: item.name.replace('(Default)', ''),
  })),
)
const tabs = computed(() =>
  allChannels.value
    .filter((item) =>
      (props.context.channels || [])
        .map((id: number | string) => +id)
        .includes(+item.value),
    )
    .map((item) => ({
      name: item.value,
      label: item.label,
    })),
)
const channel = ref(props.context._value || +tabs.value[0]?.name)
const selectItems = computed(() => [
  ...allChannels.value.filter((item) =>
    (props.context.channels || [])
      .map((id: number | string) => +id)
      .includes(+item.value),
  ),
])
const addItems = computed(() =>
  allChannels.value
    .filter(
      (item) =>
        !(props.context.channels || [])
          .map((id: number | string) => +id)
          .includes(+item.value),
    )
    .map((item) => ({
      id: item.value,
      name: item.label,
    })),
)
const addChannel = async (value: number) => {
  const { isCanceled } = await confirmDialog.reveal()

  if (isCanceled) {
    return
  }

  props.context.node.input(value)

  channel.value = value
}

const removeChannel = async (value: number) => {
  const { isCanceled } = await confirmDialog.reveal()

  if (isCanceled) {
    return
  }

  props.context.node.emit('remove', value)

  await nextTick()

  channel.value = tabs.value[0].name
}

const channelChange = (channel) => {
  props.context.node.input(channel)
  // router.replace({ query: { channel } }); // A lot of problems here..
}
channelChange(channel.value)
</script>

<template>
  <section
    class="flex items-center bg-gray-100 shadow rounded-lg my-4 py-2 sticky top-0 backdrop-filter backdrop-blur ring-1 ring-black ring-opacity-5"
    :class="{ 'py-4 flex justify-between': !context.channels?.length }"
  >
    <template v-if="context.channels?.length">
      <TabsNavigation
        v-model="channel"
        :tabs="tabs"
        class="hidden md:flex flex-grow overflow-hidden px-2"
        @update:model-value="channelChange"
      />
    </template>
    <div v-else class="ml-4 md:ml-6">
      <h2 class="text-lg font-medium text-gray-900">Channels</h2>
      <p class="mt-1 text-sm text-gray-500">Add channel from the list</p>
    </div>
    <div class="flex gap-2 mr-4">
      <FormKit
        v-if="context.channels?.length"
        v-model="channel"
        type="multiselect"
        name="channelId"
        :ignore="true"
        :can-clear="false"
        searchable
        placeholder="Select channel"
        :options="selectItems"
        :classes="{
          outer: 'w-full md:w-[200px] mb-0',
        }"
        @update:model-value="channelChange"
      >
        <template #option="{ option }">
          <div class="flex justify-between items-center w-full">
            <span>{{ option.label }}</span>
            <Button
              class="place-self-end p-0.5 text-gray-300 group"
              :class="
                option.disabled ? 'hover:bg-green-500' : 'hover:bg-red-500'
              "
              variant="simple"
              size="small"
              @click.prevent.stop="
                option.disabled
                  ? addChannel(option.value)
                  : removeChannel(option.value)
              "
            >
              <div
                v-if="option.disabled"
                class="multiselect-clear-icon i-heroicons-solid-plus group-hover:text-white"
              />
              <div
                v-else
                class="multiselect-clear-icon i-heroicons-solid-minus group-hover:text-white"
              />
            </Button>
          </div>
        </template>
      </FormKit>
      <Combobox :items="addItems" @update:model-value="addChannel($event.id)">
        <Button variant="gray" class="!px-2 text-gray-700 h-full">
          <div class="i-icon-park-outline:plus" />
        </Button>
      </Combobox>
    </div>
  </section>
  <FormKit type="group" name="channelGroup">
    <!-- <component :is="context.slots.default" /> -->
    <slot />
  </FormKit>
</template>
